import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  addDoc,
  where,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type {
  InventoryItem,
  StockMovement,
  StockMovementType,
} from '@/types/schemas';

function tsToMs(v: unknown): number | undefined {
  if (!v) return undefined;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

function mapInventoryDoc(id: string, data: Record<string, unknown>): InventoryItem {
  return {
    id,
    ...(data as Omit<InventoryItem, 'id'>),
    createdAt: tsToMs(data.createdAt),
    updatedAt: tsToMs(data.updatedAt),
    archivedAt: data.archivedAt ? tsToMs(data.archivedAt) : null,
  };
}

export async function fetchAllInventory(): Promise<InventoryItem[]> {
  const snap = await getDocs(
    query(
      collection(db, 'datahub_inventory'),
      where('isActive', '==', true),
      orderBy('itemName'),
    ),
  );
  return snap.docs.map((d) => mapInventoryDoc(d.id, d.data() as Record<string, unknown>));
}

export async function fetchArchivedInventory(): Promise<InventoryItem[]> {
  const snap = await getDocs(
    query(
      collection(db, 'datahub_inventory'),
      where('isActive', '==', false),
      orderBy('itemName'),
    ),
  );
  return snap.docs.map((d) => mapInventoryDoc(d.id, d.data() as Record<string, unknown>));
}

export async function fetchLowStock(): Promise<InventoryItem[]> {
  // lowStock is only ever true for a small slice of the catalog, so filtering
  // server-side here (rather than fetching every item) keeps this proportional
  // to the result size, not the catalog size.
  const snap = await getDocs(
    query(collection(db, 'datahub_inventory'), where('lowStock', '==', true)),
  );
  return snap.docs
    .map((d) => mapInventoryDoc(d.id, d.data() as Record<string, unknown>))
    .filter((i) => i.isActive !== false);
}

function formatSku(codePrefix: string, seq: number): string {
  return `${codePrefix}-${seq}`;
}

export async function assignSku(codePrefix: string): Promise<string> {
  const prefix = codePrefix.trim().toUpperCase();
  if (!prefix || prefix.length < 2 || prefix.length > 5) {
    throw new Error('codePrefix must be 2-5 uppercase letters');
  }
  if (!/^[A-Z]+$/.test(prefix)) {
    throw new Error('codePrefix must be uppercase letters only');
  }

  const ref = doc(db, 'settings', 'counters');
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const key = `sku_${prefix}`;
    const cur = (snap.exists() && (snap.data()?.[key] as number)) || 0;
    const next = cur + 1;
    tx.set(ref, { [key]: next }, { merge: true });
    return next;
  });

  return formatSku(prefix, seq);
}

export async function createInventoryItem(
  data: Omit<InventoryItem, 'id'>,
  uid?: string | null,
  options?: { storeId?: string; initialStock?: number },
): Promise<string> {
  const { storeId, initialStock = 0 } = options ?? {};
  const ref = await addDoc(collection(db, 'datahub_inventory'), {
    ...data,
    quantity: 0,
    lowStock: false,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid ?? null,
  });

  if (initialStock > 0 && storeId) {
    await recordStockMovement({
      itemId: ref.id,
      storeId,
      type: 'RESTOCK_IN',
      quantity: initialStock,
      note: 'Opening stock on product create',
    });
  }

  return ref.id;
}

export async function updateInventoryItem(
  id: string,
  data: Partial<InventoryItem>,
): Promise<void> {
  const rest = { ...data } as Record<string, unknown>;
  delete rest.quantity;
  delete rest.lowStock;
  await updateDoc(doc(db, 'datahub_inventory', id), {
    ...rest,
    updatedAt: serverTimestamp(),
  });
}

export async function archiveInventoryItem(id: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'datahub_inventory', id), {
    isActive: false,
    archivedAt: serverTimestamp(),
    archivedBy: uid,
    updatedAt: serverTimestamp(),
  });
}

export async function restoreInventoryItem(id: string): Promise<void> {
  await updateDoc(doc(db, 'datahub_inventory', id), {
    isActive: true,
    archivedAt: null,
    archivedBy: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteInventoryItem(id: string): Promise<void> {
  await deleteDoc(doc(db, 'datahub_inventory', id));
}

export function storeInventoryDocId(storeId: string, itemId: string): string {
  return `${storeId}_${itemId}`;
}

export async function getStoreInventory(
  storeId: string,
  itemId: string,
): Promise<{ quantity: number } | null> {
  const ref = doc(db, 'store_inventory', storeInventoryDocId(storeId, itemId));
  const s = await getDoc(ref);
  return s.exists() ? (s.data() as { quantity: number }) : null;
}

export async function fetchStoreInventoryMap(storeId: string): Promise<Record<string, number>> {
  const snap = await getDocs(
    query(collection(db, 'store_inventory'), where('storeId', '==', storeId)),
  );
  const map: Record<string, number> = {};
  snap.docs.forEach((d) => {
    const data = d.data() as { itemId: string; quantity: number };
    map[data.itemId] = data.quantity;
  });
  return map;
}

/** Merge global items with per-store quantities for POS / product lists */
export function mergeWithStoreStock(
  items: InventoryItem[],
  storeMap: Record<string, number>,
): InventoryItem[] {
  return items.map((item) => {
    const storeQty = storeMap[item.id];
    if (storeQty !== undefined) {
      return { ...item, quantity: storeQty };
    }
    return item;
  });
}

export async function recordStockMovement(input: {
  itemId: string;
  storeId: string;
  type: StockMovementType;
  quantity: number;
  note: string;
  supplierId?: string;
  referenceId?: string;
  serialNumbers?: string[];
  actorId?: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, 'stock_movements'), {
    itemId: input.itemId,
    storeId: input.storeId,
    type: input.type,
    quantity: input.quantity,
    note: input.note,
    supplierId: input.supplierId ?? null,
    referenceId: input.referenceId ?? null,
    serialNumbers: input.serialNumbers ?? null,
    actorId: input.actorId ?? null,
    applied: false,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function fetchStockMovements(
  itemId: string,
  storeId?: string,
): Promise<StockMovement[]> {
  const snap = await getDocs(
    query(collection(db, 'stock_movements'), where('itemId', '==', itemId), limit(200)),
  );
  let list = snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      ...(data as Omit<StockMovement, 'id'>),
      createdAt: tsToMs(data.createdAt),
      appliedAt: tsToMs(data.appliedAt),
    };
  });
  if (storeId) list = list.filter((m) => m.storeId === storeId);
  return list.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export async function migrateInventoryLedger(): Promise<{
  skuBackfilled: number;
  movementsCreated: number;
  itemsProcessed: number;
}> {
  const fn = httpsCallable<undefined, {
    skuBackfilled: number;
    movementsCreated: number;
    itemsProcessed: number;
  }>(functions, 'migrateInventoryLedger');
  const result = await fn();
  return result.data;
}
